import { NgClass } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { EditableContent } from 'components/input/editable-content/editable-content.component';
import { DataRowComponent, DataTableComponent } from 'components/layout/data-table/data-table.component';
import { InputType } from 'enums/InputType';
import { ReplyType } from 'enums/ReplyType';
import { ApprovalStatus, InputTypeApprovalModel } from 'models/generated/Models.InputTypeData.InputTypeApproval';
import { QuestionViewModel } from 'models/generated/Models.QuestionnaireView';
import { SelectListItemModel } from 'models/SelectList';
import { Subscription } from 'rxjs';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl } from 'services/navigation.service';
import { PushService } from 'services/push.service';
import { QuestionnaireService } from 'services/questionnaire.service';
import { QuestionnaireInputComponent, ReplyView } from './questionnaire-input.component';
import { QuestionnaireReplyComponent } from './questionnaire-reply.component';

@Component({
	selector: 'questionnaire-question',
	templateUrl: './questionnaire-question.component.html',
	imports: [EditableContent, NgClass, DataTableComponent, DataRowComponent, QuestionnaireReplyComponent, QuestionnaireInputComponent, CustomInputComponent]
})
@ComponentUrl('questionnaire-question')
export class QuestionnaireQuestionComponent implements OnDestroy {

	private _question?: QuestionViewModel;
	private replyChangeSubscription: Subscription;
	public isAnswered = false;
	public isRejected = false;
	public replyType = ReplyType;
	public selectList?: SelectListItemModel[];
	public replyGroups: ReplyView[] = [];

	@Input() set question(question: QuestionViewModel | undefined) {
		if (!question) return;
		
		this._question = question;
		if (question.questionReplyInputType == ReplyType.Select) {
			this.questionnaireService.getQuestionById(question.questionId!).then(questionData => {
				this.selectList = questionData.questionReplyOptions.map(option => new SelectListItemModel(option.replyText, option.replyText));
			});
		}

		this.prepareReplies();
	}
	get question() { return this._question; }
	
	@Input() editable = false;
	@Input() displayAsForm = false;
	@Input() loading = false;
	
	get inputType() { return this.question ? <InputType>(this.question.questionReplyInputType as number) : 0; }

	markAsPristine() { } // No "pristine" state on questionnaires

	public refreshAnsweredState() {
		this.isAnswered = !this.replyGroups.some(group => !this.checkIsAnsweredForGroup(group.replies)); // Double-negative: Question is answered if there are no groups that aren't answered :)
		if (this.isAnswered) this.isRejected = false;
		this.formValidation.fieldStateChanged(this, this.isAnswered, false);
	}
	private checkIsAnsweredForGroup(replies?: unknown[]): boolean {
		if (!replies || !this.question) return false;

		if (this.question.questionReplyInputType === ReplyType.AddMultiple) {
			return (replies as string[][]).filter(reply => reply != undefined && reply != null).length >= this.question.requiredNumberOfReplies;
		}
		if (this.question.questionReplyInputType === ReplyType.Approval) {
			if ((replies as InputTypeApprovalModel[]).some(reply => reply && reply.approvalStatus === ApprovalStatus.Rejected)) {
				this.isRejected = true;
				return false;
			}
		}
		if (this.question.questionReplyInputType === ReplyType.TrueFalse) {
			return !(replies as (boolean | undefined)[]).some(reply => reply === undefined);
		}
		return replies.filter(reply => reply !== null && reply !== undefined && reply !== '' && reply !== false).length >= this.question.requiredNumberOfReplies;

	}

	public async updateReplies(groups: ReplyView[]) {
		if (!this.question?.questionId) return;
		this.loading = true;
		this.replyGroups = groups;

		// Update replies for each group, if multiple products were grouped together for a single question view
		await Promise.all(groups.map(group =>
			group.replies ? this.questionnaireService.updateQuestionReply(this.question!.questionId!, group.replies, group.companyId, group.orderId, group.orderLineId, group.taskflowId) : Promise.resolve()
		));

		this.loading = false;
		this.refreshAnsweredState();
	}
	public async updateCheckbox(group: ReplyView, index: number, value: boolean) {
		group.replies[index] = value;
		await this.updateReplies(this.replyGroups);
	}

	private prepareReplies() {
		if (!this.question) return;

		// Parse JSON data for reply groups
		this.replyGroups = this.question.replyData.map((r, i) => {
			return {
				index: i + 1,
				companyId: r.companyId!,
				orderId: r.orderId,
				orderLineId: r.orderLineId,
				taskflowId: r.taskflowId,
				replies: r.replies ? JSON.parse(r.replies).slice(0, this.question!.requiredNumberOfReplies) : [],
				checkBoxReplies: r.replies ? JSON.parse(r.replies) : []
			};
		});

		// Ensure a dummy entry exists for each entry in the replies array
		for (let i = 0; i < this.question.requiredNumberOfReplies; i++) {
			for (let group of this.replyGroups) {
				switch (this.question.questionReplyInputType) {

					case ReplyType.Checkbox:
						group.checkBoxReplies[i] = group.replies[i] == true;
						break;
					default:
						group.replies[i] = group.replies[i] ?? undefined;
				}
				
			}
		}

		this.refreshAnsweredState();
	}

	constructor(private readonly questionnaireService: QuestionnaireService, private readonly formValidation: FormValidationService, pushService: PushService) {

		this.replyChangeSubscription = pushService.onQuestionReplyChange.subscribe(async (event) => {

			const data = event.data;
			if (data.questionId && data.questionId != this.question?.questionId) return;

			const matchingReply = this.question?.replyData
				.find(reply =>
					reply.companyId == data.companyId &&
					reply.orderId == data.orderId &&
					reply.orderLineId == data.orderLineId &&
					reply.taskflowId == data.taskflowId);

			if (!matchingReply) return;

			matchingReply.replies = (await this.questionnaireService.getQuestionReply(data.questionId, data.companyId, data.orderId, data.orderLineId, data.taskflowId)).replyData;
			this.prepareReplies();
		});
	}

	ngOnDestroy() {
		this.replyChangeSubscription.unsubscribe();
	}
}
